import API from "@aws-amplify/api"
import { graphqlOperation } from "@aws-amplify/api-graphql"
import { faCamera } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import imageCompression from "browser-image-compression"
import { ErrorMessage, Field, Formik } from "formik"
import React, { useState } from "react"
import { Button, Form } from "react-bootstrap"
import { useHistory } from "react-router"
import styled from "styled-components"
import * as Yup from "yup"
import { adminCreateUser } from "../../graphql-custom/mutations"
import { rmpToast, rmpToastError } from "../RMPToasts/rmptoasts"

const COMPRESSION_OPTIONS = {
    maxSizeMB: 0.3,
    maxWidthOrHeight: 800,
    useWebWorker: true,
}

const Styles = styled.div`
    label {
        font-weight: bold;
        display: block;
        margin-bottom: 4px;
    }
    input {
        width: 100%;
        box-sizing: border-box;
        border-radius: 6px;
        border: 1px solid #ddd;
        padding: 6px 10px;
        font-family: inherit;
        transition: border-color 0.2s;
        :focus {
            outline: none;
            border-color: #aaa;
        }
    }
    .field-row {
        margin-bottom: 16px;
    }
    .error-message {
        font-size: 0.8em;
        color: red;
        margin-top: 3px;
    }
    .profile-pic-preview {
        display: block;
        margin-top: 8px;
        border-radius: 50%;
        object-fit: cover;
    }
    input[type=file] {
        display: none;
    }
    .upload-label {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        color: #6d84b4;
        font-weight: normal;
        margin-top: 4px;
    }
`

const schema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string().required('Password is required').min(8, 'At least 8 characters'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Please confirm password'),
})

export default function AddUserForm() {
    const history = useHistory()
    const [profileFile, setProfileFile] = useState(null)
    const [profilePreview, setProfilePreview] = useState(null)
    const [compressing, setCompressing] = useState(false)

    const handleProfilePicChange = async e => {
        const file = e.target.files[0]
        if (!file) return
        const types = ['image/png', 'image/jpeg', 'image/gif']
        if (!types.includes(file.type)) {
            rmpToastError('Unsupported image format')
            return
        }
        setCompressing(true)
        const compressed = await imageCompression(file, COMPRESSION_OPTIONS)
        setCompressing(false)
        setProfileFile(compressed)
        setProfilePreview(URL.createObjectURL(compressed))
    }

    const handleSubmit = async (values, { setSubmitting }) => {
        let profilePictureBase64 = null
        let profilePictureContentType = null

        if (profileFile) {
            const buffer = await profileFile.arrayBuffer()
            profilePictureBase64 = Buffer.from(buffer).toString('base64')
            profilePictureContentType = profileFile.type
        }

        try {
            const res = await API.graphql(graphqlOperation(adminCreateUser, {
                username: values.username,
                email: values.email,
                password: values.password,
                profilePictureBase64,
                profilePictureContentType,
            }))
            const result = res.data.adminCreateUser
            if (result.success) {
                rmpToast('User created!', { autoClose: 2000, hideProgressBar: true })
                setTimeout(() => history.push('/'), 1500)
            } else {
                rmpToastError(result.message || 'Failed to create user')
            }
        } catch (err) {
            console.error(err)
            rmpToastError('Error creating user')
        }
        setSubmitting(false)
    }

    return <Styles>
        <Formik
            initialValues={{ username: '', email: '', password: '', confirmPassword: '' }}
            validationSchema={schema}
            onSubmit={handleSubmit}
            validateOnChange={false}
        >
            {({ handleSubmit, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>

                <div className='field-row'>
                    <label>Username</label>
                    <Field name='username' component='input' placeholder='username'/>
                    <div className='error-message'><ErrorMessage name='username'/></div>
                </div>

                <div className='field-row'>
                    <label>Email</label>
                    <Field name='email' component='input' type='email' placeholder='email@example.com'/>
                    <div className='error-message'><ErrorMessage name='email'/></div>
                </div>

                <div className='field-row'>
                    <label>Password</label>
                    <Field name='password' component='input' type='password' placeholder='••••••••'/>
                    <div className='error-message'><ErrorMessage name='password'/></div>
                </div>

                <div className='field-row'>
                    <label>Confirm Password</label>
                    <Field name='confirmPassword' component='input' type='password' placeholder='••••••••'/>
                    <div className='error-message'><ErrorMessage name='confirmPassword'/></div>
                </div>

                <div className='field-row'>
                    <label>Profile Picture <em style={{fontWeight: 'normal', color: '#aaa'}}>— optional</em></label>
                    <label className='upload-label' htmlFor='profile-pic-input'>
                        <FontAwesomeIcon icon={faCamera}/>
                        {compressing ? 'Compressing...' : profileFile ? 'Change photo' : 'Upload / Take photo'}
                    </label>
                    <input
                        id='profile-pic-input'
                        type='file'
                        accept='image/*'
                        capture='environment'
                        onChange={handleProfilePicChange}
                    />
                    {profilePreview && <img
                        className='profile-pic-preview'
                        src={profilePreview}
                        height='80px'
                        width='80px'
                        alt='preview'
                    />}
                </div>

                <center>
                    <Button type='submit' variant='dark' disabled={isSubmitting || compressing}>
                        {isSubmitting ? 'Creating...' : 'Create User'}
                    </Button>
                </center>

            </Form>
            )}
        </Formik>
    </Styles>
}
